<template>
    <div>
      <div class="p-fluid p-grid">
        <div class="p-field p-col-12 p-md-6">
          <label for="monthSelect">Select Month:</label>
          <Dropdown 
            v-model="selectedMonth" 
            :options="nepaliMonths" 
            optionLabel="name" 
            placeholder="Select Month" 
            @change="handleMonthChange" 
          />
        </div>
  
        <div class="p-field p-col-12 p-md-6">
          <label for="weekSelect">Select Week:</label>
          <Dropdown 
            v-model="selectedWeek" 
            :options="weeks" 
            optionLabel="name" 
            placeholder="Select Week" 
            :disabled="!weeklyData.length"
            @change="handleWeekChange" 
          />
        </div>
      </div>
  
      <div class="p-fluid p-grid">
        <div class="p-col-12 p-md-6 chart-container">
          <h4>Daily Target vs Achieved (Bar)</h4>
          <canvas id="weekly-target-bar"></canvas>
        </div>
        
        <div class="p-col-12 p-md-6 chart-container">
          <h4>Daily Target Trend (Line)</h4>
          <canvas id="weekly-target-line"></canvas>
        </div>
      </div>
  
      <!-- New Combined Charts -->
      <div class="p-fluid p-grid mt-4">
        <div class="p-col-12 chart-container">
          <h4>Combined Daily Target vs Achieved (Bar)</h4>
          <canvas id="combined-target-bar"></canvas>
        </div>
      </div>
  
      <div class="p-fluid p-grid mt-4">
        <div class="p-col-12 chart-container">
          <h4>Combined Daily Target Trend (Line)</h4>
          <canvas id="combined-target-line"></canvas>
        </div>
      </div>
    </div>
  </template>
  
  <script>
  import Chart from 'chart.js'
  import Dropdown from 'primevue/dropdown'
  
  export default {
    name: 'WeeklyTargetCharts',
    components: { Dropdown },
    data() {
      return {
        selectedMonth: null,
        selectedWeek: null,
        weeks: [],
        weeklyData: [],
        targetBarChart: null,
        targetLineChart: null,
        combinedBarChart: null,
        combinedLineChart: null,
        nepaliMonths: [
        { name: 'Baisakh', value: 1 },
        { name: 'Jestha', value: 2 },
        { name: 'Ashadh', value: 3 },
        { name: 'Shrawan', value: 4 },
        { name: 'Bhadra', value: 5 },
        { name: 'Ashwin', value: 6 },
        { name: 'Kartik', value: 7 },
        { name: 'Mangsir', value: 8 },
        { name: 'Poush', value: 9 },
        { name: 'Magh', value: 10 },
        { name: 'Falgun', value: 11 },
        { name: 'Chaitra', value: 12 }
        ]
      }
    },
    methods: {
      async handleMonthChange() {
        this.weeklyData = []
        this.weeks = []
        this.selectedWeek = null
        if (!this.selectedMonth) return
        try {
          const selectedMonthName = this.selectedMonth ? this.selectedMonth.name : null
          const response = await this.$store.dispatch(
            'loadDailyTargetAchievedBar',
            { month: selectedMonthName }
          )
          console.log(response)
          this.weeklyData = response
          this.weeks = this.weeklyData.map((_, index) => ({
            name: `Week ${index + 1}`,
            value: index
          }))
          console.log(this.weeks)
          if (this.weeks.length > 0) {
            this.selectedWeek = this.weeks[0]
            this.updateCharts()
          }
        } catch (error) {
          console.error('Error loading weekly data:', error)
        }
      },
      handleWeekChange() {
        this.updateCharts()
      },
      updateCharts() {
        this.destroyCharts()
        if (!this.selectedWeek || !this.weeklyData.length) return
        const weekData = this.weeklyData[this.selectedWeek.value]
        this.createBarChart(weekData)
        this.createLineChart(weekData)
        this.createCombinedCharts(this.weeklyData)
      },
      createBarChart(data) {
        const ctx = document.getElementById('weekly-target-bar')
        this.targetBarChart = new Chart(ctx, {
          type: 'bar',
          data: {
            labels: data.labels,
            datasets: data.datasets.map(dataset => ({
              ...dataset,
              backgroundColor: dataset.label.includes('Target')
                ? 'rgba(63, 94, 191, 0.7)'
                : 'rgba(66, 179, 244, 0.7)',
              borderColor: dataset.label.includes('Target')
                ? '#3F5EBF'
                : '#42b3f4',
              borderWidth: 1
            }))
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            scales: {
              y: {
                beginAtZero: true,
                title: { display: true, text: 'Amount' }
              }
            },
            plugins: {
              title: {
                display: true,
                text: `Week ${this.selectedWeek.value + 1} Target vs Achieved`
              }
            }
          }
        })
      },
      createLineChart(data) {
        const ctx = document.getElementById('weekly-target-line')
        this.targetLineChart = new Chart(ctx, {
          type: 'line',
          data: {
            labels: data.labels,
            datasets: data.datasets.map(dataset => ({
              ...dataset,
              borderWidth: 2,
              fill: false,
              tension: 0.4,
              pointRadius: 4,
              borderColor: dataset.label.includes('Target')
                ? '#3F5EBF'
                : '#42b3f4'
            }))
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            scales: {
              y: {
                beginAtZero: true,
                title: { display: true, text: 'Amount' }
              }
            },
            plugins: {
              title: {
                display: true,
                text: `Week ${this.selectedWeek.value + 1} Daily Trends`
              }
            }
          }
        })
      },
      createCombinedCharts(allWeekData) {
        if (!allWeekData || !allWeekData.length) return
        // Combine all data from all weeks
        const combinedLabels = []
        const combinedTargetData = []
        const combinedAchievedData = []
        allWeekData.forEach(weekData => {
          weekData.labels.forEach((label, index) => {
            combinedLabels.push(label)
            combinedTargetData.push(weekData.datasets[0].data[index])
            combinedAchievedData.push(weekData.datasets[1].data[index])
          })
        })
        // Create combined bar chart
        const barCtx = document.getElementById('combined-target-bar')
        this.combinedBarChart = new Chart(barCtx, {
          type: 'bar',
          data: {
            labels: combinedLabels,
            datasets: [
              {
                label: 'Daily Target',
                data: combinedTargetData,
                backgroundColor: 'rgba(63, 94, 191, 0.7)',
                borderColor: '#3F5EBF',
                borderWidth: 1
              },
              {
                label: 'Daily Achieved',
                data: combinedAchievedData,
                backgroundColor: 'rgba(66, 179, 244, 0.7)',
                borderColor: '#42b3f4',
                borderWidth: 1
              }
            ]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            scales: {
              y: {
                beginAtZero: true,
                title: { display: true, text: 'Amount' }
              }
            },
            plugins: {
              title: {
                display: true,
                text: `Monthly Target vs Achieved (${this.selectedMonth.name || 'All'})`
              }
            }
          }
        })
        // Create combined line chart
        const lineCtx = document.getElementById('combined-target-line')
        this.combinedLineChart = new Chart(lineCtx, {
          type: 'line',
          data: {
            labels: combinedLabels,
            datasets: [
              {
                label: 'Daily Target',
                data: combinedTargetData,
                borderWidth: 2,
                fill: false,
                tension: 0.4,
                pointRadius: 4,
                borderColor: '#3F5EBF'
              },
              {
                label: 'Daily Achieved',
                data: combinedAchievedData,
                borderWidth: 2,
                fill: false,
                tension: 0.4,
                pointRadius: 4,
                borderColor: '#42b3f4'
              }
            ]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            scales: {
              y: {
                beginAtZero: true,
                title: { display: true, text: 'Amount' }
              }
            },
            plugins: {
              title: {
                display: true,
                text: `Monthly Target vs Achieved Trend (${this.selectedMonth.name || 'All'})`
              }
            }
          }
        })
      },
      destroyCharts() {
        if (this.targetBarChart) {
          this.targetBarChart.destroy()
          this.targetBarChart = null
        }
        if (this.targetLineChart) {
          this.targetLineChart.destroy()
          this.targetLineChart = null
        }
        if (this.combinedBarChart) {
          this.combinedBarChart.destroy()
          this.combinedBarChart = null
        }
        if (this.combinedLineChart) {
          this.combinedLineChart.destroy()
          this.combinedLineChart = null
        }
      }
    },
    beforeUnmount() {
      this.destroyCharts()
    }
  }
  </script>
  
  <style scoped>
  .chart-container {
    background: white;
    padding: 20px;
    border-radius: 10px;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    margin-top: 1rem;
    min-height: 400px;
    position: relative;
  }
  
  h4 {
    margin: 0 0 1rem 0;
    color: #2c3e50;
    font-size: 1.2rem;
  }
  
  canvas {
    width: 100% !important;
    height: 350px !important;
  }
  
  .mt-4 {
    margin-top: 2rem;
  }
  </style>



// WEBPACK FOOTER //
// DailySalesReportGraph.vue?e6900608